import { useUiTranslation } from 'lib/i18n/context';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from 'components/ui/skeleton';
import {
    SessionOrganization,
    useLogout,
    useSessionOrganizations,
    useUser,
} from 'lib/auth';
import { Building2, ChevronsUpDown, Loader2, LogOut } from 'lucide-react';
import { useStytchB2BClient } from '@stytch/react/b2b';
import { useIsMutating, useQueryClient } from '@tanstack/react-query';

import { Avatar, AvatarFallback } from 'components/ui/avatar';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from 'components/ui/dropdown';
import {
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    useSidebar,
} from 'components/ui/sidebar';
import { getInitials } from 'lib/utils';
import { LanguageMenuItems } from 'lib/i18n/language-selector';

export function NavUser() {
    const { t } = useUiTranslation('common');

    const navigate = useNavigate();
    const user = useUser();
    const stytch = useStytchB2BClient();
    const queryClient = useQueryClient();
    const sessionOrganizations = useSessionOrganizations();
    const pendingMutations = useIsMutating();

    const onBackendLogoutSuccess = useCallback(() => {
        queryClient.clear();
        stytch.session.revoke();
        navigate('/authenticate');
    }, [navigate, queryClient, stytch.session]);
    const logout = useLogout(onBackendLogoutSuccess);
    const { isMobile } = useSidebar();

    const onLogout = useCallback(() => {
        logout.mutate();
    }, [logout]);

    const onDropdownOpenChange = useCallback(
        (open: boolean) => {
            if (
                open &&
                !sessionOrganizations.data &&
                !sessionOrganizations.isFetching
            ) {
                void sessionOrganizations.refetch();
            }
        },
        [sessionOrganizations],
    );

    const onSwitchOrganization = useCallback(
        (organization: SessionOrganization) => {
            navigate('/switch-organization', {
                state: {
                    targetOrganization: organization,
                    sourceStytchOrganizationId:
                        sessionOrganizations.data
                            ?.current_stytch_organization_id,
                },
            });
        },
        [navigate, sessionOrganizations.data],
    );

    if (user.isLoading || !user.data) {
        return <Skeleton className="h-10 w-full" />;
    }

    return (
        <SidebarMenu>
            <SidebarMenuItem>
                <DropdownMenu modal={false} onOpenChange={onDropdownOpenChange}>
                    <DropdownMenuTrigger asChild>
                        <SidebarMenuButton
                            size="lg"
                            className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                        >
                            <Avatar className="h-8 w-8 rounded-lg">
                                {/* <AvatarImage
                                    src={user.data.avatar}
                                    alt={user.data.name}
                                /> */}
                                <AvatarFallback className="rounded-lg bg-secondary">
                                    {getInitials(user.data.name)}
                                </AvatarFallback>
                            </Avatar>
                            <div className="grid flex-1 text-left text-sm leading-tight">
                                <span className="truncate font-semibold">
                                    {user.data.name}
                                </span>
                                <span className="truncate text-xs">
                                    {user.data.email}
                                </span>
                            </div>
                            <ChevronsUpDown className="ml-auto size-4" />
                        </SidebarMenuButton>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                        side={isMobile ? 'bottom' : 'right'}
                        align="end"
                        sideOffset={4}
                    >
                        <LanguageMenuItems />
                        <DropdownMenuLabel className="p-0 font-normal">
                            <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                                <Avatar className="h-8 w-8 rounded-lg">
                                    {/* <AvatarImage
                                        src={user.data.avatar}
                                        alt={user.data.name}
                                    /> */}
                                    <AvatarFallback className="rounded-lg">
                                        {getInitials(user.data.name)}
                                    </AvatarFallback>
                                </Avatar>
                                <div className="grid flex-1 text-left text-sm leading-tight">
                                    <span className="truncate font-semibold">
                                        {user.data.name}
                                    </span>
                                    <span className="truncate text-xs">
                                        {user.data.email}
                                    </span>
                                </div>
                            </div>
                            <div className="flex items-center px-1 py-1">
                                {
                                    <span className="truncate text-xs">
                                        {user.data.organization.name}
                                    </span>
                                }
                            </div>
                        </DropdownMenuLabel>
                        <DropdownMenuSub>
                            <DropdownMenuSubTrigger
                                disabled={pendingMutations > 0}
                            >
                                <Building2 className="mr-2 h-4 w-4" />
                                {t(($) => $.switch_organization)}
                            </DropdownMenuSubTrigger>
                            <DropdownMenuSubContent className="min-w-56">
                                {sessionOrganizations.isFetching && (
                                    <DropdownMenuItem disabled>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        {t(($) => $.loading_organizations)}
                                    </DropdownMenuItem>
                                )}
                                {sessionOrganizations.isError && (
                                    <DropdownMenuItem
                                        onSelect={() =>
                                            void sessionOrganizations.refetch()
                                        }
                                    >
                                        {t(($) => $.unable_to_load_try_again)}
                                    </DropdownMenuItem>
                                )}
                                {sessionOrganizations.data &&
                                    sessionOrganizations.data.organizations
                                        .filter(
                                            (organization) =>
                                                organization.stytch_organization_id !==
                                                sessionOrganizations.data
                                                    ?.current_stytch_organization_id,
                                        )
                                        .map((organization) => (
                                            <DropdownMenuItem
                                                key={
                                                    organization.stytch_organization_id
                                                }
                                                disabled={
                                                    pendingMutations > 0 ||
                                                    logout.isPending
                                                }
                                                onSelect={() =>
                                                    onSwitchOrganization(
                                                        organization,
                                                    )
                                                }
                                            >
                                                {organization.name}
                                            </DropdownMenuItem>
                                        ))}
                                {sessionOrganizations.data &&
                                    sessionOrganizations.data.organizations.every(
                                        (organization) =>
                                            organization.stytch_organization_id ===
                                            sessionOrganizations.data
                                                ?.current_stytch_organization_id,
                                    ) && (
                                        <DropdownMenuItem disabled>
                                            {t(($) => $.no_other_organizations)}
                                        </DropdownMenuItem>
                                    )}
                            </DropdownMenuSubContent>
                        </DropdownMenuSub>
                        <DropdownMenuItem
                            onClick={onLogout}
                            disabled={logout.isPending || pendingMutations > 0}
                        >
                            <LogOut className="mr-2" />
                            {t(($) => $.log_out)}
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </SidebarMenuItem>
        </SidebarMenu>
    );
}
